// ============================================================================
// geometry/dumbbell.js — code-generated round-head dumbbell (Phase F, product 3)
// ============================================================================
// Two kinds share ONE generator (Ryan 2026-07-14): a `rubber` flag is the only
// difference. Modeled from Ryan's spec + two reference charts (FITSHOP TF-NPDB
// and the HomeFitTools round-head table). Ryan's steer: "I don't need it exact
// to the mm — get the PROPORTIONS and SHAPE right; my supplier ships the real
// product (with a centered logo baked on the end cap)." So every number below is
// a proportion the eye reads, not a manufacturing tolerance.
//
//   LONG AXIS = Z (revolve about Z, exactly like bumper-plate.js). The two round
//   END-CAP faces (±Z) are the BRAND faces — the centered logo, both ends.
//
//   PROFILE from center outward (mirrored to each end; s = ±1 picks the end):
//   ├─ knurled steel SHAFT   r = handleRadius (Ø32 mm), knurl = texture (smooth mesh)
//   ├─ smooth band           r = handleRadius   (~⅜″ before the flare)
//   ├─ FLARE OUT ~45°        r grows over ~½″ to a collar peak (chrome collar)
//   ├─ TUCK back IN          r necks over ~⅜″ to meet the head's inner face
//   ├── head INNER face      annulus (rubber/urethane), faces back toward center
//   ├── head RIM (both)      rounded edges (corner radius, like the plate)
//   ├── head CYLINDER wall    r = headRadius
//   └── head OUTER face       the BRAND end-cap:
//         urethane → a perfectly FLAT disc (logo edge-to-edge, centered)
//         rubber   → a raised center BOSS that "juts out" (logo sits ON the boss)
//
//   Head Ø and thickness both GROW with weight, but Ø caps (~20 cm) once the
//   plate would hit the floor — past that only thickness grows (see the chart).
//   Grip length (exposed steel) is ~constant; total length = grip + 2·thickness.
//
// THREE MATERIAL SLOTS (mirror the plate so reveal-3d's material rig just works):
//   0 BrandFace — the two end-cap discs/bosses (planar UV, the design)
//   1 body      — black satin rubber / urethane head (walls, faces, rims)
//   2 metal     — the knurled steel handle + flared chrome collars
//
// PURE (no THREE / WebGL) so it is node-testable (test/dumbbell.smoke.mjs); the
// THREE factory takes THREE as an argument. Winding is bulletproof: every quad is
// emitted with a desired outward normal and auto-reversed if the geometric normal
// opposes it (same machinery as bumper-plate.js).
// ============================================================================

/** Default real-world dimensions (metres) + tessellation. Hero ≈ a chunky
 *  mid-heavy dumbbell (≈15 kg proportions from the HomeFitTools chart). */
export const DEFAULT_DUMBBELL_OPTS = Object.freeze({
  rubber: true, // true = rubber round-head (raised boss); false = urethane (flat face)

  // --- handle (both kinds): knurled steel, Ø32 mm × ~6″ ---------------------
  // The collar is WIDEST where it meets the head and narrows monotonically to the
  // shaft — a single chamfer, no outward peak (Ryan 2026-07-14 silhouette fix).
  handleRadius: 0.016, // 32 mm dia shaft
  gripLength: 0.145, // exposed steel between the two heads (~5.7″); ≈ constant by weight
  collarRadius: 0.023, // collar's max radius, at the head face (~46 mm dia seat)
  collarLength: 0.011, // axial run of the shaft→collar chamfer (the "flute")
  collarCylLength: 0.0055, // straight cylinder at collarRadius between the flute and the head (Ryan)
  knurlInset: 0.0055, // knurl stops this far short of each flute (a smooth polished band) — Ryan
  // Explicit knurl BANDS along the shaft, as [zFrom, zTo] pairs in metres from the
  // bar's centre (may be negative; clamped to the shaft and emitted in order).
  // `null` = the dumbbell's original behaviour: ONE band filling the shaft, inset
  // by `knurlInset` at each end. Fixed barbells mark the handle in discrete bands
  // instead (Ryan 2026-07-15), so they pass a list — see geometry/barbell.js.
  knurlZones: null,
  // Bends the bar: `(z) => dy` in metres, displacing the profile sideways as a
  // function of position along the axis. `null` = dead straight (dumbbells, straight
  // barbells). EZ-curl bars pass a W profile — see geometry/barbell.js.
  //
  // Applied as a position+normal post-pass that SWEEPS each ring square to the local
  // tangent (rotate about X by β = atan(shaftOffset'(z))), so band boundaries + knurl
  // rows tilt with the bar instead of staying vertical (see the post-pass at build
  // end). Still O(verts), no sweep engine — every ring's offset from the axis is
  // (x, yLocal, 0) at that point, so the rotation is exact. The offset (and β) must be
  // ~0 outside the shaft so the flutes, collars and heads — bodies of revolution about
  // Z — keep a sub-degree tilt at the shaft ends and are otherwise left untouched.
  shaftOffset: null,
  // How many rings to spread along the shaft when `shaftOffset` bends it. A STRAIGHT
  // shaft needs none — two rings per band chord a straight line perfectly, which is
  // why the shaft is otherwise tessellated only at the marking-zone boundaries. A BENT
  // shaft with the same rings chords straight THROUGH the curve: the first EZ build
  // had no ring between t=±0.275, so the mesh flew straight over the centre dip and
  // rendered a flat plateau where a W should be (caught 2026-07-15).
  // Ignored entirely when shaftOffset is null, so dumbbells/straight bars pay nothing.
  //
  // 96 is chosen against a reference the eye has already accepted: the shaft's RADIAL
  // faceting is `radialSegments` = 96 around, i.e. 3.75°/facet, and that reads as
  // perfectly round. So the AXIAL crease — the angle between neighbouring rings, which
  // is what shows up as "jagged" — only has to get under that. Measured on the EZ bend
  // (tightest radius ~7.2"):
  //     32 segs -> 11.43°   (visibly kinked)
  //     64 segs ->  6.47°
  //     96 segs ->  4.65°   ~= the roundness already accepted   <- here
  //    128 segs ->  3.61°   but 22.7 ms/rebuild vs 16.3
  // Cost is a rebuild (denomination change), not a per-frame cost: 96 segs = 28.6k tris
  // at 16.3 ms, against the shipping bumper plate's 12.9k at 8.6 ms. 128 pays ~3x the
  // baseline to chase a crease already below the shaft's own faceting.
  //
  // NOTE this counts for nothing if the profile itself has a corner in it — a table
  // with a kink renders a kink at any segment count. See EZ_CURL_PROFILE's note on the
  // trace notch that cost exactly that lesson.
  shaftAxialSegments: 96,
  // Metal RING on the head's inner face around the handle joint (Ryan 2026-07-14):
  // a 5.5 mm-wide flush ring, RECESSED 0.5 mm into the head so the rubber sits proud.
  innerRingWidth: 0.0055, // radial width of the inner-face metal ring
  innerRingRecess: 0.0005, // how far the ring is sunk below the rubber inner face

  // --- head (varies by weight; hero = 15 kg row) ----------------------------
  headRadius: 0.085, // 170 mm dia head
  headThickness: 0.065, // 65 mm thick disc (urethane; = the plain flat-face depth)
  headEdgeRadius: 0.0045, // rounded rim on the INNER (handle-side) edge + urethane's flat-face edge (Ryan: ~50% less; inner + outer match)
  edgeSegments: 8, // sub-segments per quarter-round rim (Ryan: actually round + smooth, not faceted)

  // --- rubber-only terraced end-cap (Ryan 2026-07-14): from the cylindrical side,
  //     the face ANGLES in (chamfer), FLATTENS ~3/16″, steps UP ~3/16″, then
  //     FLATTENS across the center (logo) — every turn softened by a slight radius.
  //     The raised center is why a rubber head is a touch deeper than urethane. ---
  faceChamferDrop: 0.0135, // vertical drop of the outer "angled end" chamfer (Ryan: ~75% longer + steeper)
  faceChamferRun: 0.011, // radial inward run of the chamfer
  faceLedgeWidth: 0.0048, // flat ledge after the chamfer (dimensions locked — Ryan)
  faceRiserRun: 0, // the step-up riser is VERTICAL (Ryan: not slanted)
  faceRiserRise: 0.0048, // riser height == the ledge width (Ryan: "same height as the flat ring was wide")
  faceCornerRadius: 0.0016, // "slight radius" filleting every turn
  faceCapRadius: 0.0012, // very slight round-over where the riser meets the flat art canvas (Ryan)
  faceCornerSegs: 4, // arc sub-segments per softened turn (smooth, not faceted)

  radialSegments: 96, // revolve smoothness
});

/** Head dimensions (metres) by weight, PROPORTIONAL from the HomeFitTools chart:
 *  kg → [head DIAMETER, head THICKNESS]. `dumbbellHeadDims` halves the diameter
 *  to a RADIUS for the generator. Grip length stays ~constant, so total length =
 *  grip + 2·thickness. Rebuilt on denomination change. Not mm-exact — the
 *  supplier finishes to spec. */
export const DUMBBELL_HEAD_DIMS_KG = Object.freeze({
  2.5: [0.111, 0.034],
  5: [0.124, 0.046],
  7.5: [0.137, 0.051],
  10: [0.155, 0.057],
  12.5: [0.162, 0.06],
  15: [0.17, 0.065],
  17.5: [0.178, 0.068],
  20: [0.185, 0.072],
  22.5: [0.193, 0.074],
  25: [0.199, 0.075],
  27.5: [0.203, 0.078],
  30: [0.203, 0.085],
  35: [0.203, 0.098],
  40: [0.203, 0.112],
  45: [0.203, 0.122],
  50: [0.203, 0.134],
  /* ⚠ THE FOUR ROWS BELOW ARE DERIVED, NOT MEASURED (2026-08-02). Everything above is a real
     supplier dimension; the chart stopped at 50 kg (110 lb) and Ryan's dumbbell ladder runs to
     150 lb (68 kg), which left a fifth of the range clamping silently to the 110's head.
       Both facts are extrapolated from the region where the head is ALREADY at its cap:
       • DIAMETER — flat 0.203 m since 30 kg. Above the cap a heavier dumbbell gets thicker, not
         wider, which is the whole reason the chart goes flat there; carrying it on is reading the
         data rather than guessing at it.
       • THICKNESS — a least-squares fit over the five capped rows (30→50 kg) gives
         t = 0.0126 + 0.00244·kg, with the samples sitting within 1.5 mm of that line. Evaluated
         at 55/60/65/70.
       They are the honest continuation of a straight line, and they are still not measurements.
     ⚠ Beyond ~110 lb the real product may widen again rather than only thicken — a 150 lb head
     capped at 8" is an assumption worth Ryan's eye. It is edge-on-only in the designer (the
     BRANDED FACE is identical at every denomination above 66 lb, cap being cap), so the cost of
     being wrong is a silhouette, not a misleading proof. */
  55: [0.203, 0.147],
  60: [0.203, 0.159],
  65: [0.203, 0.171],
  70: [0.203, 0.183],
});

/** Pounds per kilogram. Named because three places used to spell it 2.205 independently. */
export const LB_PER_KG = 2.20462;
/** The charted span, ascending — the interpolation's knots. */
const CHART_KG = Object.freeze(Object.keys(DUMBBELL_HEAD_DIMS_KG).map(Number).sort((a, b) => a - b));
export const DUMBBELL_CHART_MAX_KG = CHART_KG[CHART_KG.length - 1];

/**
 * Is this load inside the charted span, or would it silently clamp to an end?
 *
 * Replaces a membership test against `LB_TO_KG` — see that constant's note for why exact
 * membership stopped being the right question once the lookup interpolated.
 */
export function isChartedDumbbellLoad(weight, unit = 'lb') {
  const kg = unit === 'kg' ? Number(weight) : Number(weight) / LB_PER_KG;
  if (!(kg > 0)) return false;
  // ABOVE the top row there is nothing to interpolate between: the head clamps to the heaviest
  // charted one and says nothing, which is exactly the silent-wrong this guard exists to catch.
  if (kg > DUMBBELL_CHART_MAX_KG) return false;
  // BELOW the bottom row it clamps to the lightest head, and that is right by construction: 5 lb
  // is 2.268 kg, the lightest dumbbell in the line, and the 2.5 kg row IS its head — which is the
  // plate Ryan has already signed off. Accepted deliberately rather than by omission.
  return true;
}

// LB denominations map to their nearest kg row (dumbbells the shop sells are
// spec'd in lb; the chart is metric). ~2.205 lb/kg, rounded to a charted row.
//
// EXPORTED (2026-07-30) so `products.js` can ASSERT that every denomination it offers the
// designer lands on a real row of DUMBBELL_HEAD_DIMS_KG. `dumbbellHeadDims` falls back to the
// nearest charted row, which is the right runtime behaviour and a silent one: an uncharted
// denomination renders a neighbouring head and says nothing. The only way to catch that is to
// check membership in THIS table and in the chart, which needs both to be visible from outside.
// Nothing else about it changed — it is still the same object the lookup below reads.
export const LB_TO_KG = { 5: 2.5, 10: 5, 15: 7.5, 20: 10, 25: 12.5, 30: 12.5, 35: 15, 40: 17.5, 45: 20, 50: 22.5, 55: 25 };

/**
 * Resolves [headRadius, headThickness] (metres) for a weight + unit, halving the charted
 * DIAMETER to a radius. INTERPOLATES between the two bracketing rows; clamps at either end.
 *
 * ⛔ IT USED TO SNAP TO THE NEAREST ROW, AND THAT COLLAPSED DENOMINATIONS ONTO EACH OTHER.
 *
 * The old path was `LB_TO_KG[weight] || weight / 2.205` then nearest-row — a lookup table that
 * rounded each pound value to the closest 2.5 kg knot. On the eleven denominations that existed
 * it mostly worked and in one place it did not: 25 lb (11.34 kg) and 30 lb (13.61 kg) BOTH
 * rounded to the 12.5 kg row, so the two rendered as the same dumbbell. products.js already
 * carried the observation — *"25 and 30 lb both land on 12.5 kg, which is the chart's own doing,
 * not a typo"* — as a curiosity.
 *   It stopped being a curiosity on 2026-08-02, when Ryan's real ladder arrived: 5–150 lb in
 * fives plus the five half-steps, 35 rungs onto 20 knots. Snapping would have made roughly half
 * of them visually identical to a neighbour — on a control whose entire purpose is answering
 * "does the 25 look like the 45?". A ladder that lies about that is worse than no ladder.
 *
 * The chart was always a SAMPLED CURVE, not a set of allowed values; reading it as the latter was
 * the mistake. Interpolating also removes the second-guessing from `barbellHeadDims`, which has
 * always passed a continuous residual load through here and been quietly rounded.
 *
 * ⚠ EXISTING HEADS MOVE SLIGHTLY. A 10 lb was snapping to the 5 kg row — 11.02 lb of head on a
 * 10 lb dumbbell — and now interpolates to its true 4.536 kg. Differences are low single-digit
 * percent everywhere except 25/30 lb, which stop being twins and separate by ~7 mm of diameter.
 * That is the fix, not a side effect, but it does mean previously-approved renders shift.
 */
export function dumbbellHeadDims(weight, unit) {
  const kg = unit === 'kg' ? Number(weight) : Number(weight) / LB_PER_KG;
  const t = DUMBBELL_HEAD_DIMS_KG;
  const keys = CHART_KG;
  if (!(kg > keys[0])) { const e = t[keys[0]]; return [e[0] / 2, e[1]]; }
  const top = keys[keys.length - 1];
  if (kg >= top) { const e = t[top]; return [e[0] / 2, e[1]]; }
  let i = 1;
  while (i < keys.length - 1 && keys[i] < kg) i++;
  const lo = keys[i - 1];
  const hi = keys[i];
  const f = (kg - lo) / (hi - lo);
  const a = t[lo];
  const b = t[hi];
  return [(a[0] + (b[0] - a[0]) * f) / 2, a[1] + (b[1] - a[1]) * f];
}

/** Material slot indices (mirror the BufferGeometry groups). Slot 3 (knurl) is the
 *  smooth-shaft's diamond-knurled middle — same metal, but it carries cylindrical
 *  UVs so the material can tile a knurl bump/normal map onto it (the smooth bands,
 *  flute, collar + ring stay on slot 2 with no UVs, so they render clean). */
export const MATERIAL_BRAND_FACE = 0;
export const MATERIAL_BODY = 1;
export const MATERIAL_METAL = 2;
export const MATERIAL_KNURL = 3;

// --- tiny vec helpers (no deps) ---------------------------------------------
function sub(a, b) {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function cross(a, b) {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function dot(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function normalize3(v) {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

/**
 * Pure vertex-math generator for the round-head dumbbell.
 *
 * @param {Partial<typeof DEFAULT_DUMBBELL_OPTS>} [opts]
 * @returns {{
 *   positions:number[], normals:number[], uvs:number[], colors:number[], tangents:number[],
 *   indices:number[], groups:{start:number,count:number,materialIndex:number,name:string}[],
 *   triangleCount:number, vertexCount:number,
 *   brandVertexRange:{start:number,count:number}, length:number,
 *   params: typeof DEFAULT_DUMBBELL_OPTS,
 * }}
 */
export function buildDumbbellMesh(opts = {}) {
  const params = { ...DEFAULT_DUMBBELL_OPTS, ...opts };
  const rubber = !!params.rubber;
  const seg = Math.max(24, Math.floor(params.radialSegments));

  const hR = params.handleRadius;
  const HR = params.headRadius;
  const HT = params.headThickness;

  // Axial levels from center (z ≥ 0; each end multiplies by s = ±1).
  const zHeadIn = params.gripLength / 2; // head inner face (exposed-grip end)
  const zCylStart = zHeadIn - params.collarCylLength; // flute end / collar-cylinder start
  const zCham = zCylStart - params.collarLength; // where the shaft→collar flute begins
  const zHeadOut = zHeadIn + HT; // head outer face
  const rCollar = Math.min(params.collarRadius, HR - 1e-4); // collar max radius (at the head)
  const zRing = zHeadIn + params.innerRingRecess; // recessed metal-ring plane (into the head)
  const rRing = Math.min(rCollar + params.innerRingWidth, HR - 0.006); // ring outer edge

  // Rim rounding (clamped so it never exceeds the disc it rounds).
  const er = Math.max(0, Math.min(params.headEdgeRadius, 0.45 * HT, 0.4 * HR));
  const eSeg = er > 1e-6 ? Math.max(1, Math.floor(params.edgeSegments)) : 0;

  // Rubber terrace geometry (local +Z; mirrored per end). The center flat rises
  // `riserRise` above the base face plane (zHeadOut) → a rubber head is deeper.
  const chamH = rubber ? params.faceChamferDrop : 0;
  const chamW = rubber ? params.faceChamferRun : 0;
  const ledgeW = rubber ? params.faceLedgeWidth : 0;
  const riserRun = rubber ? params.faceRiserRun : 0;
  const riserRise = rubber ? params.faceRiserRise : 0;
  const cornerR = rubber ? params.faceCornerRadius : 0;
  const cornerSegs = Math.max(1, Math.floor(params.faceCornerSegs));
  // Branded center-disc radius: the raised center flat (rubber) or the whole flat
  // face inside its rounded edge (urethane).
  const brandDiscR = rubber ? Math.max(0.2 * HR, HR - chamW - ledgeW - riserRun) : HR - er; // riser top / canvas outer edge
  const capR = rubber ? Math.max(0, Math.min(params.faceCapRadius, 0.4 * riserRise, 0.3 * brandDiscR)) : 0;
  const brandR = brandDiscR - capR; // the flat art canvas begins where the cap round-over ends
  const brandZ = rubber ? zHeadOut + riserRise : zHeadOut; // where the branded surface sits
  const length = 2 * brandZ;

  // Planar UV extent for the brand face — the design fills the flat art canvas, centered.
  const uvExtent = 2 * brandR;

  const positions = [];
  const normals = [];
  const uvs = [];
  const colors = [];
  const tangents = [];
  const idxBrand = [];
  const idxBody = [];
  const idxMetal = [];
  const idxKnurl = [];
  const state = { vertCount: 0 };

  const routeIdx = (slot) =>
    slot === 'brand' ? idxBrand : slot === 'metal' ? idxMetal : slot === 'knurl' ? idxKnurl : idxBody;
  const CUV = [0.5, 0.5]; // body/metal park at texture center (solid color)
  const planarUV = (x, y) => [x / uvExtent + 0.5, y / uvExtent + 0.5];

  function pushVert(pos, normal, uv) {
    positions.push(pos[0], pos[1], pos[2]);
    const n = normalize3(normal);
    normals.push(n[0], n[1], n[2]);
    uvs.push(uv[0], uv[1]);
    colors.push(1, 1, 1); // no baked AO on the dumbbell (no deep crevices)
    // Circumferential tangent (matches the plate's turned-metal anisotropy setup).
    const tl = Math.hypot(pos[0], pos[1]) || 1;
    tangents.push(-pos[1] / tl, pos[0] / tl, 0, 1);
    state.vertCount += 1;
  }

  /** Emits one quad (2 tris) with correct outward winding (auto-reversed). */
  function pushQuad(slot, corners) {
    const desired = normalize3([
      corners[0].normal[0] + corners[1].normal[0] + corners[2].normal[0] + corners[3].normal[0],
      corners[0].normal[1] + corners[1].normal[1] + corners[2].normal[1] + corners[3].normal[1],
      corners[0].normal[2] + corners[1].normal[2] + corners[2].normal[2] + corners[3].normal[2],
    ]);
    const geo = cross(sub(corners[1].pos, corners[0].pos), sub(corners[2].pos, corners[0].pos));
    const c = dot(geo, desired) < 0 ? [corners[0], corners[3], corners[2], corners[1]] : corners;
    const base = state.vertCount;
    for (const v of c) pushVert(v.pos, v.normal, v.uv);
    routeIdx(slot).push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  /** Emits one triangle (used for the end-cap center fan). */
  function pushTri(slot, corners) {
    const desired = normalize3([
      corners[0].normal[0] + corners[1].normal[0] + corners[2].normal[0],
      corners[0].normal[1] + corners[1].normal[1] + corners[2].normal[1],
      corners[0].normal[2] + corners[1].normal[2] + corners[2].normal[2],
    ]);
    const geo = cross(sub(corners[1].pos, corners[0].pos), sub(corners[2].pos, corners[0].pos));
    const c = dot(geo, desired) < 0 ? [corners[0], corners[2], corners[1]] : corners;
    const base = state.vertCount;
    for (const v of c) pushVert(v.pos, v.normal, v.uv);
    routeIdx(slot).push(base, base + 1, base + 2);
  }

  // Shared ring of angles.
  const cosA = new Array(seg + 1);
  const sinA = new Array(seg + 1);
  for (let i = 0; i <= seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    cosA[i] = Math.cos(a);
    sinA[i] = Math.sin(a);
  }

  /** Revolves a cross-section segment A→B (each {r,z,nr,nz}) 360° about Z.
   *  `brand` → planar UVs (mirrored U on the −Z end so the logo reads correctly
   *  from behind — there is no true front/back, same as the plate). */
  function emitBand(slot, A, B, brand = false, mirrorU = false) {
    const uvAt = (x, y) => {
      if (!brand) return CUV;
      const u = planarUV(x, y);
      return mirrorU ? [1 - u[0], u[1]] : u;
    };
    for (let i = 0; i < seg; i++) {
      const c0 = cosA[i];
      const s0 = sinA[i];
      const c1 = cosA[i + 1];
      const s1 = sinA[i + 1];
      const a0 = [A.r * c0, A.r * s0, A.z];
      const a1 = [A.r * c1, A.r * s1, A.z];
      const b0 = [B.r * c0, B.r * s0, B.z];
      const b1 = [B.r * c1, B.r * s1, B.z];
      pushQuad(slot, [
        { pos: a0, uv: uvAt(a0[0], a0[1]), normal: [A.nr * c0, A.nr * s0, A.nz] },
        { pos: b0, uv: uvAt(b0[0], b0[1]), normal: [B.nr * c0, B.nr * s0, B.nz] },
        { pos: b1, uv: uvAt(b1[0], b1[1]), normal: [B.nr * c1, B.nr * s1, B.nz] },
        { pos: a1, uv: uvAt(a1[0], a1[1]), normal: [A.nr * c1, A.nr * s1, A.nz] },
      ]);
    }
  }

  /** Knurled shaft cylinder (r = hR) from zA→zB on the `knurl` slot, with CYLINDRICAL
   *  UVs: u wraps 0→1 around the circumference, v runs 0→1 along the zone. The
   *  material tiles a diamond knurl bump/normal map across these (integer repeat in u
   *  so the wrap seam matches). */
  function emitKnurlCylinder(zA, zB) {
    // V TRACKS PHYSICAL DISTANCE, not the band (Ryan 2026-07-15). U already spans
    // 0→1 around the circumference, so scaling v by that same circumference makes
    // the mapping ISOTROPIC: one repeat count (knurl-texture.js's 46) yields square
    // diamonds at ANY shaft length and across ANY number of bands, and the pattern
    // stays continuous from one band to the next because v is a function of world z.
    //
    // It used to be `v = 0→1 over the band`, which squares up only when the band
    // happens to be as long as the shaft is round — true for the dumbbell (101 mm
    // band vs 100.5 mm circumference) and false for everything else. A 40" barbell
    // stretched each diamond 9.7:1 and read as a threaded rod; four marking bands
    // would instead have crammed a full shaft's worth of diamonds into each band.
    // The dumbbell's own look is preserved to within 0.5% (101/100.5), i.e. no
    // visible change to the shipping product.
    const vScale = 2 * Math.PI * hR; // one v unit = once around the shaft
    const vA = zA / vScale;
    const vB = zB / vScale;
    for (let i = 0; i < seg; i++) {
      const c0 = cosA[i];
      const s0 = sinA[i];
      const c1 = cosA[i + 1];
      const s1 = sinA[i + 1];
      const u0 = i / seg;
      const u1 = (i + 1) / seg;
      pushQuad('knurl', [
        { pos: [hR * c0, hR * s0, zA], uv: [u0, vA], normal: [c0, s0, 0] },
        { pos: [hR * c0, hR * s0, zB], uv: [u0, vB], normal: [c0, s0, 0] },
        { pos: [hR * c1, hR * s1, zB], uv: [u1, vB], normal: [c1, s1, 0] },
        { pos: [hR * c1, hR * s1, zA], uv: [u1, vA], normal: [c1, s1, 0] },
      ]);
    }
  }

  /** Center-fan disc at z (radius r0, +Z*faceSign normal). Brand → planar UV. */
  function emitDisc(slot, r0, z, faceSign, brand = false, mirrorU = false) {
    const nz = [0, 0, faceSign];
    for (let i = 0; i < seg; i++) {
      const c0 = cosA[i];
      const s0 = sinA[i];
      const c1 = cosA[i + 1];
      const s1 = sinA[i + 1];
      const p0 = [r0 * c0, r0 * s0, z];
      const p1 = [r0 * c1, r0 * s1, z];
      const uv0 = brand ? (mirrorU ? [1 - planarUV(p0[0], p0[1])[0], planarUV(p0[0], p0[1])[1]] : planarUV(p0[0], p0[1])) : CUV;
      const uv1 = brand ? (mirrorU ? [1 - planarUV(p1[0], p1[1])[0], planarUV(p1[0], p1[1])[1]] : planarUV(p1[0], p1[1])) : CUV;
      const uvC = brand ? (mirrorU ? [1 - planarUV(0, 0)[0], planarUV(0, 0)[1]] : planarUV(0, 0)) : CUV;
      pushTri(slot, [
        { pos: [0, 0, z], uv: uvC, normal: nz },
        { pos: p0, uv: uv0, normal: nz },
        { pos: p1, uv: uv1, normal: nz },
      ]);
    }
  }

  /** Fillets the corners of a (r,z) profile polyline and revolves it as `body`.
   *  `pts` = outer→inner list of {r,z,rad}; each interior corner is rounded by its
   *  `rad` (Ryan's "slight radius to every turn/angle change"). `faceSign` mirrors
   *  the whole thing to the −Z end. STRAIGHT runs are flat-shaded; each fillet ARC
   *  carries CURVATURE-FOLLOWING per-vertex normals so it shades as an actual
   *  smooth radius (not faceted) — matching the inner rim. A flat→arc junction is a
   *  crisp tangent (the two share a position but not a normal), so no false crease.
   *  Output = a list of segments, each {A,B} with per-endpoint {r,z,nr,nz}. */
  function emitRoundedProfile(pts, faceSign) {
    const segs = [];
    // Flat outward normal for a straight run in direction (dr,dz): perpendicular,
    // oriented away from the axis (+r) and, for near-flat runs, toward +z.
    const flatNormal = (dr, dz) => {
      let nr = dz;
      let nz = -dr;
      if (nr < -1e-9 || (Math.abs(nr) < 1e-9 && nz < 0)) {
        nr = -nr;
        nz = -nz;
      }
      const l = Math.hypot(nr, nz) || 1;
      return [nr / l, nz / l];
    };
    const straight = (A, B) => {
      if (Math.hypot(B.r - A.r, B.z - A.z) < 1e-7) return; // skip zero-length
      const [nr, nz] = flatNormal(B.r - A.r, B.z - A.z);
      segs.push({ A: { r: A.r, z: A.z, nr, nz }, B: { r: B.r, z: B.z, nr, nz } });
    };
    let cursor = { r: pts[0].r, z: pts[0].z };
    for (let i = 1; i < pts.length - 1; i++) {
      const A = pts[i - 1];
      const B = pts[i];
      const C = pts[i + 1];
      const rad = B.rad || 0;
      const ba = [A.r - B.r, A.z - B.z];
      const bc = [C.r - B.r, C.z - B.z];
      const lba = Math.hypot(ba[0], ba[1]) || 1;
      const lbc = Math.hypot(bc[0], bc[1]) || 1;
      const ua = [ba[0] / lba, ba[1] / lba];
      const uc = [bc[0] / lbc, bc[1] / lbc];
      const cosPhi = Math.max(-1, Math.min(1, ua[0] * uc[0] + ua[1] * uc[1]));
      const phi = Math.acos(cosPhi); // interior angle at B
      const t = Math.min(rad / Math.tan(phi / 2), 0.49 * lba, 0.49 * lbc);
      if (!(t > 1e-6) || phi > Math.PI - 1e-3) {
        straight(cursor, { r: B.r, z: B.z }); // no meaningful corner → sharp
        cursor = { r: B.r, z: B.z };
        continue;
      }
      const rEff = t * Math.tan(phi / 2);
      const T1 = { r: B.r + t * ua[0], z: B.z + t * ua[1] };
      const T2 = { r: B.r + t * uc[0], z: B.z + t * uc[1] };
      let bis = [ua[0] + uc[0], ua[1] + uc[1]];
      const lb = Math.hypot(bis[0], bis[1]) || 1;
      bis = [bis[0] / lb, bis[1] / lb];
      const center = [B.r + bis[0] * (rEff / Math.sin(phi / 2)), B.z + bis[1] * (rEff / Math.sin(phi / 2))];
      const a1 = Math.atan2(T1.z - center[1], T1.r - center[0]);
      let a2 = Math.atan2(T2.z - center[1], T2.r - center[0]);
      while (a2 - a1 > Math.PI) a2 -= 2 * Math.PI;
      while (a2 - a1 < -Math.PI) a2 += 2 * Math.PI;
      straight(cursor, T1); // run into the fillet
      // Arc: POSITION follows the fillet circle, but the NORMAL is blended from the
      // incoming flat normal to the outgoing flat normal — always outward, so it is
      // correct for CONVEX *and* CONCAVE corners (deriving it from the arc centre
      // points inward on concave corners → back-faces → culled → a see-through gap).
      const nSeg = Math.max(1, Math.floor(B.seg || cornerSegs));
      const nIn = flatNormal(-ua[0], -ua[1]); // incoming segment's outward normal
      const nOut = flatNormal(uc[0], uc[1]); // outgoing segment's outward normal
      const angIn = Math.atan2(nIn[1], nIn[0]);
      let dN = Math.atan2(nOut[1], nOut[0]) - angIn;
      while (dN > Math.PI) dN -= 2 * Math.PI;
      while (dN < -Math.PI) dN += 2 * Math.PI;
      const arcNode = (k) => {
        const f = k / nSeg;
        const a = a1 + (a2 - a1) * f;
        const na = angIn + dN * f;
        return { r: center[0] + rEff * Math.cos(a), z: center[1] + rEff * Math.sin(a), nr: Math.cos(na), nz: Math.sin(na) };
      };
      for (let k = 0; k < nSeg; k++) segs.push({ A: arcNode(k), B: arcNode(k + 1) });
      cursor = { r: T2.r, z: T2.z };
    }
    straight(cursor, { r: pts[pts.length - 1].r, z: pts[pts.length - 1].z });
    // Revolve every segment, applying faceSign to z + the z-normal.
    for (const s of segs) {
      emitBand(
        'body',
        { r: s.A.r, z: faceSign * s.A.z, nr: s.A.nr, nz: faceSign * s.A.nz },
        { r: s.B.r, z: faceSign * s.B.z, nr: s.B.nr, nz: faceSign * s.B.nz },
      );
    }
  }

  const brandStartVert = state.vertCount;

  // ===========================================================================
  // 1. BRAND slot FIRST — the two end-cap branded discs (design), verts from 0.
  // ===========================================================================
  // +Z end reads normally; −Z end mirrors U so the logo reads from behind.
  emitDisc('brand', brandR, brandZ, 1, true, false); // +Z end cap
  emitDisc('brand', brandR, -brandZ, -1, true, true); // −Z end cap
  const brandVertexCount = state.vertCount - brandStartVert;

  // ===========================================================================
  // 2. METAL slot — knurled steel shaft + flared chrome collars (both ends).
  // ===========================================================================
  // One continuous shaft cylinder across the flat exposed grip (knurl is a texture
  // within it — smooth mesh keeps the tri budget sane).
  //
  // KNURL ZONE (Ryan 2026-07-14): the diamond criss-cross knurl fills the MIDDLE of
  // the shaft but stops `knurlInset` short of each flute, leaving a smooth polished
  // band at each end. The knurl band is its own material slot (cylindrical UVs); the
  // smooth bands stay on `metal`. `zCham` is where the flute begins.
  // `knurlZones` (barbells) lists the bands explicitly; the dumbbell's default of
  // `null` means the single inset-at-both-ends band it has always had. Both resolve
  // to the same list-of-bands shape, so ONE walk emits either: smooth `metal` across
  // every gap, `knurl` across every band. The default path emits exactly the bands
  // the old if/else did — metal(−zCham,−zKnurl) · knurl(−zKnurl,zKnurl) ·
  // metal(zKnurl,zCham) — so the dumbbell's geometry is unchanged.
  const zKnurl = Math.max(0, zCham - params.knurlInset);
  const zones = (params.knurlZones ? params.knurlZones : zKnurl > 1e-4 ? [[-zKnurl, zKnurl]] : [])
    .map(([a, b]) => [Math.max(Math.min(a, b), -zCham), Math.min(Math.max(a, b), zCham)]) // clamp to the shaft
    .filter(([a, b]) => b - a > 1e-4) // drop bands that clamped away to nothing
    .sort((p, q) => p[0] - q[0]);

  // Emits one shaft run, subdivided along z ONLY when the shaft is bent — each run
  // takes rings in proportion to its share of the shaft, so the bend is followed
  // evenly rather than per-band. Straight shafts take the n=1 path and are emitted
  // exactly as before (two rings, one band), so dumbbells are untouched.
  const shaftSpan = 2 * zCham;
  const axial = typeof params.shaftOffset === 'function' ? Math.max(1, Math.round(params.shaftAxialSegments)) : 1;
  function emitShaftRun(slot, zA, zB) {
    const n = axial > 1 ? Math.max(1, Math.ceil((Math.abs(zB - zA) / shaftSpan) * axial)) : 1;
    for (let i = 0; i < n; i++) {
      const z0 = zA + ((zB - zA) * i) / n;
      const z1 = zA + ((zB - zA) * (i + 1)) / n;
      if (slot === 'knurl') emitKnurlCylinder(z0, z1);
      else emitBand('metal', { r: hR, z: z0, nr: 1, nz: 0 }, { r: hR, z: z1, nr: 1, nz: 0 });
    }
  }

  let zCursor = -zCham;
  for (const [a, b] of zones) {
    if (a > zCursor + 1e-6) emitShaftRun('metal', zCursor, a); // smooth gap
    emitShaftRun('knurl', a, b);
    zCursor = Math.max(zCursor, b);
  }
  if (zCursor < zCham - 1e-6) emitShaftRun('metal', zCursor, zCham); // smooth tail (or the whole shaft, unknurled)
  // Each end: a CHAMFER (flute) shaft(hR) → collar(rCollar), then a straight
  // CYLINDER at rCollar into the head. Widest at the head, no outward peak. The
  // flute cone widens toward the head, so its normal tilts back to center (−s·z).
  const chDr = rCollar - hR;
  const chL = Math.hypot(chDr, params.collarLength) || 1;
  const chNr = params.collarLength / chL;
  const chNz = -chDr / chL;
  for (const s of [1, -1]) {
    emitBand('metal', { r: hR, z: s * zCham, nr: chNr, nz: s * chNz }, { r: rCollar, z: s * zCylStart, nr: chNr, nz: s * chNz }); // flute
    emitBand('metal', { r: rCollar, z: s * zCylStart, nr: 1, nz: 0 }, { r: rCollar, z: s * zRing, nr: 1, nz: 0 }); // straight collar cylinder → recessed ring plane
    // Flush metal RING around the joint, on the head inner face (recessed), faces −s.
    emitBand('metal', { r: rCollar, z: s * zRing, nr: 0, nz: -s }, { r: rRing, z: s * zRing, nr: 0, nz: -s });
  }

  // ===========================================================================
  // 3. BODY slot — the rubber/urethane head (both ends).
  // ===========================================================================
  for (const s of [1, -1]) {
    // Rubber steps up 0.5 mm PROUD of the recessed metal ring (wall faces inward),
    // then the rubber INNER face annulus runs out to the rim. (The ring itself +
    // the collar cylinder are metal, emitted above.)
    emitBand('body', { r: rRing, z: s * zRing, nr: -1, nz: 0 }, { r: rRing, z: s * zHeadIn, nr: -1, nz: 0 });
    emitBand('body', { r: rRing, z: s * zHeadIn, nr: 0, nz: -s }, { r: HR - er, z: s * zHeadIn, nr: 0, nz: -s });
    // INNER rim fillet: quarter-round from the inner face (r=HR−er, faces −z-local)
    // out to the cylinder (r=HR, faces +radial). Fillet center at (HR−er, zHeadIn+er);
    // arc a sweeps −90°→0° so the normal rolls from −z-local to +radial.
    if (eSeg > 0) {
      let prev = null;
      for (let k = 0; k <= eSeg; k++) {
        const a = -Math.PI / 2 + (Math.PI / 2) * (k / eSeg);
        const p = { r: HR - er + er * Math.cos(a), z: s * (zHeadIn + er + er * Math.sin(a)), nr: Math.cos(a), nz: s * Math.sin(a) };
        if (prev) emitBand('body', prev, p);
        prev = p;
      }
    }
    // Head CYLINDER wall (up to where the outer-face treatment begins).
    const cylTop = rubber ? zHeadOut - chamH : zHeadOut - er;
    emitBand('body', { r: HR, z: s * (zHeadIn + er), nr: 1, nz: 0 }, { r: HR, z: s * cylTop, nr: 1, nz: 0 });
    // OUTER face profile (outer→inner), body down to the branded center disc.
    if (rubber) {
      // Terrace: chamfer (angled end) → flat ledge → riser up → center flat (brand).
      emitRoundedProfile(
        [
          { r: HR, z: cylTop, rad: 0 },
          { r: HR - chamW, z: zHeadOut, rad: cornerR }, // chamfer top / ledge outer
          { r: HR - chamW - ledgeW, z: zHeadOut, rad: cornerR }, // ledge inner (flat ~3/16″)
          { r: brandDiscR, z: brandZ, rad: capR }, // riser top → canvas, very slight round-over
          { r: brandR, z: brandZ, rad: 0 }, // flat art canvas begins
        ],
        s,
      );
    } else {
      // Urethane: a single rounded edge (quarter-round) into a perfectly flat face.
      emitRoundedProfile(
        [
          { r: HR, z: zHeadOut - er, rad: 0 },
          { r: HR, z: zHeadOut, rad: er, seg: eSeg }, // quarter-round; eSeg segments to MATCH the inner rim exactly
          { r: brandDiscR, z: zHeadOut, rad: 0 }, // flat face to the branded disc (brandDiscR = HR−er)
        ],
        s,
      );
    }
  }

  // --- assemble: brand → body → metal → knurl (contiguous, non-overlapping) ---
  const indices = idxBrand.concat(idxBody, idxMetal, idxKnurl);
  const sBody = idxBrand.length;
  const sMetal = sBody + idxBody.length;
  const sKnurl = sMetal + idxMetal.length;
  const groups = [
    { start: 0, count: idxBrand.length, materialIndex: MATERIAL_BRAND_FACE, name: 'BrandFace' },
    { start: sBody, count: idxBody.length, materialIndex: MATERIAL_BODY, name: 'body' },
    { start: sMetal, count: idxMetal.length, materialIndex: MATERIAL_METAL, name: 'metal' },
    { start: sKnurl, count: idxKnurl.length, materialIndex: MATERIAL_KNURL, name: 'knurl' },
  ];
  // BEND (EZ-curl): SWEEP each cross-section along the bent centreline so it sits
  // SQUARE TO THE LOCAL TANGENT — not merely shifted sideways (Ryan 2026-07-16:
  // "if the bar is bending down at 30°, the knurling line needs to do the same").
  //
  // A pure `y += shaftOffset(z)` slides every ring sideways but leaves it in a plane
  // perpendicular to Z, so every band boundary + knurl row stayed vertical on screen
  // regardless of the bend. Instead we rotate each ring about X by the centreline's
  // local pitch β = atan(shaftOffset'(z)): the ring's in-plane axes become e1=(1,0,0)
  // and e2=(0,cosβ,−sinβ), whose plane normal is the tangent (0,sinβ,cosβ). Because
  // the knurl + smooth bands are meshed/textured onto these rings, their edges now
  // tilt with the bar automatically — most at the steep flanks, ~flat at the ends.
  //
  // Positions AND normals both transform by Rx(−β). Every ring vertex's offset from
  // the axis is (x, yLocal, 0) here (pre-bend, flat at constant z), so the map is
  // exact — no sweep engine, still one O(verts) post-pass. shaftOffset (and thus β)
  // is ~0 at the shaft ends, so the flutes/collars follow with a sub-degree tilt and
  // the heads — where the clamped profile is flat, β=0 — stay bodies of revolution.
  // `length` (an axial measure of the un-bent profile) is a fair approximation still.
  if (typeof params.shaftOffset === 'function') {
    const off = params.shaftOffset;
    const h = 2e-4; // central-difference step for the tangent slope (m); ≪ ring spacing
    for (let i = 0; i < positions.length; i += 3) {
      const z = positions[i + 2];
      const yLocal = positions[i + 1]; // pre-bend y (r·sinθ) — the ring is centred on the axis
      const slope = (off(z + h) - off(z - h)) / (2 * h);
      const beta = Math.atan(slope);
      const cb = Math.cos(beta);
      const sb = Math.sin(beta);
      // position = centre(0, shaftOffset(z), z) + Rx(−β)·(x, yLocal, 0)
      positions[i + 1] = off(z) + yLocal * cb;
      positions[i + 2] = z - yLocal * sb;
      // normal = Rx(−β)·n  (keeps shading square to the tilted ring)
      const ny = normals[i + 1];
      const nz = normals[i + 2];
      normals[i + 1] = ny * cb + nz * sb;
      normals[i + 2] = -ny * sb + nz * cb;
    }
  }

  return {
    positions,
    normals,
    uvs,
    colors,
    tangents,
    indices,
    groups,
    triangleCount: indices.length / 3,
    vertexCount: state.vertCount,
    brandVertexRange: { start: brandStartVert, count: brandVertexCount },
    brandRadius: brandR,
    length,
    params,
  };
}

/**
 * Assembles a THREE.BufferGeometry from the pure mesh data. THREE is passed in
 * (not imported) so this module never pulls in the WebGL layer for node tests.
 *
 * @param {typeof import('three')} THREE
 * @param {Partial<typeof DEFAULT_DUMBBELL_OPTS>} [opts]
 * @returns {import('three').BufferGeometry}
 */
export function createDumbbellGeometry(THREE, opts = {}) {
  const mesh = buildDumbbellMesh(opts);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(mesh.positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(mesh.normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(mesh.uvs, 2));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(mesh.colors, 3));
  geometry.setAttribute('tangent', new THREE.Float32BufferAttribute(mesh.tangents, 4));
  geometry.setIndex(mesh.indices);
  for (const g of mesh.groups) geometry.addGroup(g.start, g.count, g.materialIndex);
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();
  geometry.userData.dumbbell = { params: mesh.params, triangleCount: mesh.triangleCount, length: mesh.length, brandRadius: mesh.brandRadius };
  return geometry;
}
